import { contextItemRoute } from './contextItems';

/**
 * The things the general chat links to. The agent writes each one as a markdown
 * link with a `gaia://` target (see the backend's general-chat system prompt —
 * the two lists must stay in sync), and the thread renders it as a chip.
 */
export type ChatLinkKind =
  | 'contract'
  | 'contractFile'
  | 'matter'
  | 'signingRequest'
  | 'playbook'
  | 'playbookRun'
  | 'calendar';

export interface ChatLink {
  kind: ChatLinkKind;
  /** App path to push — the router is the host's job. */
  path: string;
}

/**
 * A canonical UUID and nothing else: 8-4-4-4-12 hex digits, case-insensitive.
 * `^…$` anchor the whole string, so no extra characters (`/`, `..`, `?`, `#`)
 * can ride along into the path built from it. Fixed-length classes, so it
 * cannot backtrack.
 */
const UUID_RE = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

/**
 * `gaia://<kind>/<rest>`, anchored at both ends:
 * - `([a-z-]+)` captures the kind: letters and hyphens only, so it cannot hold
 *   `/`, `.`, `_` or anything else; it is then looked up in `KINDS`.
 * - `\/(.+)` captures everything after the kind's slash; it is split on `/`
 *   and every piece must pass `UUID_RE`.
 * The two captures are separated by a literal `/` the first class cannot
 * contain, so there is only one way to match: linear time.
 */
const GAIA_HREF_RE = /^gaia:\/\/([a-z-]+)\/(.+)$/i;

/** gaia://<segment>/<uid>[/<uid>] — the segment names the kind; `route` takes exactly `uids` uids. */
const KINDS: Record<
  string,
  { kind: ChatLinkKind; uids: number; route: (uids: string[]) => string } | undefined
> = {
  contract: { kind: 'contract', uids: 1, route: ([uid]) => contextItemRoute('contract', uid) },
  'contract-file': {
    kind: 'contractFile',
    uids: 1,
    route: ([uid]) => contextItemRoute('contractFile', uid),
  },
  matter: {
    kind: 'matter',
    uids: 1,
    route: ([uid]) => `/contract-management/matter/${uid}/detail`,
  },
  'signing-request': { kind: 'signingRequest', uids: 1, route: ([uid]) => `/signing/${uid}` },
  // A playbook's own page is its runs list — where the Playbooks list opens it too.
  playbook: { kind: 'playbook', uids: 1, route: ([uid]) => `/repository/playbooks/${uid}/runs` },
  'playbook-run': {
    kind: 'playbookRun',
    uids: 2,
    route: ([playbookUid, runUid]) => `/repository/playbooks/${playbookUid}/runs/${runUid}`,
  },
  calendar: {
    kind: 'calendar',
    uids: 1,
    route: ([uid]) => `/contract-management/calendar/${uid}`,
  },
};

/**
 * A `gaia://` href onto its app route. Anything malformed — unknown kind, wrong
 * number of segments, a uid the model mangled — is null, so the caller shows
 * the link text as plain prose instead of a chip that leads nowhere.
 */
export function parseGaiaLink(href: string): ChatLink | null {
  const match = GAIA_HREF_RE.exec(href.trim());
  if (!match) return null;
  // Own keys only: a plain index would also find inherited ones, so
  // `gaia://constructor/…` would resolve to Object's constructor.
  const key = match[1].toLowerCase();
  const entry = Object.hasOwn(KINDS, key) ? KINDS[key] : undefined;
  if (!entry) return null;
  const uids = match[2].split('/');
  if (uids.length !== entry.uids || !uids.every(uid => UUID_RE.test(uid))) return null;
  return { kind: entry.kind, path: entry.route(uids) };
}

/**
 * Streaming view, pass 1: every finished link, anywhere, becomes its name.
 * - `\[([^[\]\n]*)\]` captures the link text: no brackets or newline inside,
 *   so each match stops at the next `[` or `]`. That keeps the global
 *   replace linear: without `[` in the class, a long run of `[` made it rescan
 *   the rest of the line from every one of them.
 * - `\(gaia:\/\/[^)\s]*\)` is the target: `gaia://`, then anything up to the
 *   closing `)` that isn't whitespace.
 */
const FINISHED_LINK_RE = /\[([^[\]\n]*)\]\(gaia:\/\/[^)\s]*\)/gi;

/**
 * Streaming view, pass 2: the answer ends inside a `gaia://` target that has
 * not got its `)` yet, e.g. `[NDA](gaia://contract/0b6e3c` becomes `NDA`. Same
 * pieces as pass 1, with the closing `)` swapped for the end-of-text anchor `$`.
 */
const OPEN_TARGET_RE = /\[([^[\]\n]*)\]\(gaia:\/\/[^)\s]*$/i;

/**
 * Streaming view, pass 3: the answer ends while the scheme itself is still
 * arriving (`[NDA](`, `[NDA](ga`, `[NDA](gaia:/`). `g?a?i?a?:?\/{0,2}` is each
 * character of `gaia://` made optional in order. It is deliberately loose (it
 * also takes `[NDA](gi`): the cost is an ordinary link showing as its name for
 * one token until its target starts, which the next token corrects.
 */
const OPEN_SCHEME_RE = /\[([^[\]\n]*)\]\(g?a?i?a?:?\/{0,2}$/i;

/**
 * The raw text shown while an answer streams: each finished `[name](gaia://…)`
 * becomes its name, and a link whose target is still arriving loses the
 * half-written target — either way no uid reaches the screen. Nothing here is
 * evaluated: `replace` only swaps matched text for the captured name.
 */
export function stripGaiaLinks(text: string): string {
  return text
    .replaceAll(FINISHED_LINK_RE, '$1')
    .replace(OPEN_TARGET_RE, '$1')
    .replace(OPEN_SCHEME_RE, '$1');
}
